import React, { useState, useMemo, useEffect } from "react";
import { isValid, differenceInMinutes } from "date-fns";
import { getLoadRequestDetailsByID } from "apis/myRequestsService";
import { getTimeAgo } from "utils/dateUtils";
import { MyRequestResponse, MyRequestItem } from "pages/YourRequest/types";
import { useNavigate } from "react-router-dom";
import DefaultAirLogo from "../../assets/images/logo.svg";
import { useToastStore } from "store/toastStore";
import { Clock } from "lucide-react";

// Skeleton loader components
const SkeletonBox = ({ width = "100%", height = "1rem", className = "" }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} style={{ width, height }} />
);

const FlightCardSkeleton = () => (
  <div className="mt-6 bg-white dark:bg-white p-5 rounded-lg w-full border-2 border-gray-100">
    <div className="grid grid-cols-12 gap-6 items-center justify-between">
      {/* Airline section */}
      <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
        <SkeletonBox width="48px" height="48px" className="rounded" />
        <div className="text-left">
          <SkeletonBox width="60px" height="12px" className="mb-2" />
          <SkeletonBox width="120px" height="16px" />
        </div>
      </div>

      {/* Flight details section */}
      <div className="flex items-start lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-4 md:col-span-8 col-span-12 gap-6">
        <div className="min-w-[100px]">
          <SkeletonBox width="70px" height="12px" className="mb-2" />
          <SkeletonBox width="80px" height="16px" />
        </div>
        <div className="min-w-[170px]">
          <SkeletonBox width="40px" height="12px" className="mb-2" />
          <SkeletonBox width="150px" height="16px" />
        </div>
        <div className="flex flex-col items-start">
          <SkeletonBox width="50px" height="12px" className="mb-2" />
          <SkeletonBox width="80px" height="28px" className="rounded-tr-lg rounded-bl-lg" />
        </div>
      </div>

      {/* Route section */}
      <div className="flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3 justify-end">
        <div className="flex flex-col gap-1">
          <SkeletonBox width="60px" height="16px" />
          <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
            <SkeletonBox width="16px" height="16px" className="rounded-full" />
            <SkeletonBox width="40px" height="12px" />
          </div>
        </div>
        <div>
          <div className="flex items-center w-[250px]">
            <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
            <SkeletonBox width="100%" height="2px" className="mx-2" />
            <SkeletonBox width="16px" height="16px" />
            <SkeletonBox width="100%" height="2px" className="mx-2" />
            <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
          </div>
          <SkeletonBox width="60px" height="12px" className="mt-1 mx-auto" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <SkeletonBox width="60px" height="16px" />
          <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
            <SkeletonBox width="16px" height="16px" className="rounded-full" />
            <SkeletonBox width="40px" height="12px" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AnsweredFlightSkeleton = () => (
  <div className="p-5 border-t border-gray-300 mt-3">
    <div className="grid grid-cols-12 gap-6 items-end justify-between">
      <div className="flex flex-col gap-1 lg:col-span-2 md:col-span-8 col-span-12">
        <SkeletonBox width="80px" height="12px" className="mb-1" />
        <SkeletonBox width="120px" height="16px" className="mb-2" />
        <div className="flex items-center gap-1">
          <SkeletonBox width="12px" height="12px" />
          <SkeletonBox width="60px" height="12px" />
        </div>
      </div>
      <div className="flex items-center lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-5 md:col-span-5 col-span-12 gap-6">
        <div className="flex flex-col gap-2">
          <SkeletonBox width="80px" height="12px" />
          <div className="flex items-center gap-2">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                <SkeletonBox width="20px" height="16px" className="mb-1" />
                <SkeletonBox width="30px" height="12px" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SkeletonBox width="150px" height="12px" />
          <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-full rounded-md">
            <SkeletonBox width="20px" height="16px" className="mb-1" />
            <SkeletonBox width="30px" height="12px" />
          </div>
        </div>
      </div>
      <div className="lg:col-span-4 md:col-span-12 col-span-12 gap-3">
        <div className="flex items-center justify-between gap-4 border p-3 border-lightgray-100 rounded-md">
          <SkeletonBox width="80px" height="16px" />
          <SkeletonBox width="80px" height="24px" className="rounded-sm" />
        </div>
      </div>
    </div>
  </div>
);

// Define FlightDetails based on API response from getLoadRequestDetailsByID
interface FlightDetails {
  responderName: string;
  airlineName: string;
}

interface FlightListingProps {
  flights: MyRequestResponse;
  show?: boolean;
  FlightLoading?: boolean;
}

const statusColors: Record<string, string> = {
  Expired: "bg-red-600",
  "Request Updating": "bg-orange-500",
  "Update Requested": "bg-[#F59E0B]",
  Answered: "bg-green-500",
  "Updated Answer": "bg-green-500",
  Pending: "bg-yellow-500",
  default: "bg-gray-500",
};

// Pill styling for the desktop card's Status column, matching the Figma spec.
const statusPillClasses: Record<string, string> = {
  Answered: "bg-[rgba(218,246,238,0.5)] text-[#009C6D]",
  "Updated Answer": "bg-blue-100 text-blue-700",
  Pending: "bg-[#FFF0F1] text-[#DC4043]",
  Expired: "bg-[#FFF0F1] text-[#DC4043]",
  "Request Updating": "bg-[#FEF3C7] text-[#C2410C]",
  "Update Requested": "bg-[#FEF3C7] text-[#C2410C]",
  default: "bg-gray-100 text-gray-600",
};

const FlightListing: React.FC<FlightListingProps> = ({ flights, show, FlightLoading }) => {
  const showToast = useToastStore((state) => state.showToast);
  const [flightDataMap, setFlightDataMap] = useState<Record<number, FlightDetails>>({});
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});
  const navigate = useNavigate();
  const limitedFlights = useMemo(() => (Array.isArray(flights) ? flights.slice(0, 5) : []), [flights]);
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
  // Fetch FlightDetails for Answered flights

  useEffect(() => {
    setIsAccordionOpen(!show); // Use show prop, default to false if undefined
  }, [show]);

  useEffect(() => {
    const answeredFlights = limitedFlights.filter(
      (flight) => flight.status === "Answered" || flight.status === "Updated Answer",
    );
    if (answeredFlights.length === 0) return;

    const fetchAnsweredFlightData = async () => {
      const newIsLoading = { ...isLoading };
      const newFlightDataMap = { ...flightDataMap };

      answeredFlights.forEach((flight) => {
        if (!flightDataMap[flight.id] && !isLoading[flight.id]) {
          newIsLoading[flight.id] = true;
        }
      });
      setIsLoading(newIsLoading);

      const promises = answeredFlights.map(async (flight) => {
        const id = flight.id;
        if (!flightDataMap[id] && !isLoading[id]) {
          try {
            const data = await getLoadRequestDetailsByID(flight.requestDetailId);
            return { id, data };
          } catch (error) {
            console.error(`Error fetching details for flight ${id}:`, error);
            return { id, data: null };
          }
        }
        return null;
      });

      const results = await Promise.all(promises);

      results.forEach((result) => {
        if (result) {
          // Only access result.id if result is not null
          if (result.data) {
            newFlightDataMap[result.id] = result.data;
          }
          newIsLoading[result.id] = false; // Safe: result is guaranteed to have id
        }
      });

      setFlightDataMap(newFlightDataMap);
      setIsLoading(newIsLoading);
    };

    fetchAnsweredFlightData();
  }, [limitedFlights]); // Remove flightDataMap and isLoading from dependencies

  const getFlightDuration = (departure: string, arrival: string): string => {
    const departureTime = new Date(departure);
    const arrivalTime = new Date(arrival);
    const durationMs = arrivalTime.getTime() - departureTime.getTime();
    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatCreatedDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "";
      // Read the raw wall-clock (UTC) so the DATE matches the times, which are
      // also rendered with timeZone: "UTC". Avoids browser-local day shifts.
      const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
      const day = String(date.getUTCDate()).padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${day} ${month} ${year}`; // "dd MMMM yyyy"
    } catch {
      return "";
    }
  };

  const handleCardClick = (flight: any) => {
    if (!flight?.id) {
      navigate("/my-loads");
      return;
    }
    navigate(`/my-loads?requestId=${flight.id}`, {
      state: { targetRequestId: flight.id, flightId: flight.id },
    });
  };


  return (
    <section className="mt-4 md:mt-6">
      <details open={isAccordionOpen} className="mt-4 md:mt-6 bg-white dark:bg-white p-4 rounded-lg w-full">
        <summary className="text-base md:text-xl font-semibold text-[#262626] mb-4 cursor-pointer">
          Recently Posted Requests
        </summary>
        <div>
          {limitedFlights.length === 0 && !FlightLoading ? (
            <div className="flex justify-center items-center h-24 md:h-32 w-full">
              <p className="text-gray-500 text-sm md:text-base">No requests found.</p>
            </div>
          ) : FlightLoading ? (
            // Show skeleton loaders while flights are loading
            [...Array(3)].map((_, index) => <FlightCardSkeleton key={`skeleton-${index}`} />)
          ) : (
            limitedFlights.map((flight: any) => (
              <div
                key={flight.id}
                className="mt-4 bg-white rounded-lg w-full border border-[#ECECED] hover:border-gray-300 transition-all cursor-pointer overflow-hidden"
                onClick={() => handleCardClick(flight)}
              >
                {/* Mobile layout — stacked so all info is visible */}
                <div className="flex md:hidden flex-col gap-3 p-4">
                  {/* Top row: airline + status */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        className="w-10 h-10 object-contain rounded-full flex-shrink-0"
                        src={flight.airlineLogo || DefaultAirLogo}
                        alt={flight.airlineName || "Airline"}
                        onError={(e) => (e.currentTarget.src = DefaultAirLogo)}
                      />
                      <div className="min-w-0">
                        <p className="text-grease-100 text-sm font-semibold leading-tight truncate">
                          {flight.airlineName || "Unknown"}
                        </p>
                        <div className="flex items-center gap-1.5 min-w-0 mt-0.5">
                          <p className="text-gray-500 text-xs truncate">{flight.flightNo}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <p
                        className={`text-white text-[10px] leading-tight py-1 px-2 rounded-tr-lg rounded-bl-lg rounded-tl-none rounded-br-none w-fit max-w-[96px] whitespace-normal break-words text-center ${
                          statusColors[flight.status] || statusColors.default
                        }`}
                      >
                        {flight.status}
                      </p>
                    </div>
                  </div>

                  {/* Route row: departure → arrival */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <p className="text-grease-100 text-base font-bold">{flight.sourceCode}</p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {flight.departure
                          ? new Date(flight.departure).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                    <div className="flex flex-col items-center flex-1 px-2">
                      <div className="flex items-center w-full">
                        <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                        <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
                        <i className="st-flight-slim text-grease-100 text-sm mx-1" />
                        <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
                        <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                      </div>
                      <p className="text-gray-500 text-[10px] text-center mt-1">
                        {flight?.elptim && (
                          <span>
                            {flight.elptim.includes(":")
                              ? `${flight.elptim.split(":")[0]}h ${flight.elptim.split(":")[1]}m`
                              : `${parseInt(flight.elptim.slice(0, -2))}h ${parseInt(flight.elptim.slice(-2))}m`}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <p className="text-grease-100 text-base font-bold">{flight.destinationCode}</p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {flight.arrival
                          ? new Date(flight.arrival).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>

                  {/* Date row */}
                  <div className="flex items-center gap-2 border-t border-gray-200 pt-2">
                    <span className="text-gray-500 text-xs">Date</span>
                    <span className="text-grease-100 text-sm font-medium">
                      {formatCreatedDate(flight.departure)}
                    </span>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden md:block px-3 py-[3.5px]">
                  <div className="grid items-center gap-4 lg:gap-6 xl:gap-8 grid-cols-[minmax(100px,1.15fr)_minmax(110px,1.35fr)_minmax(80px,0.9fr)_minmax(58px,0.7fr)_minmax(100px,2.4fr)_minmax(58px,0.7fr)]">
                  {/* Airline Logo & Info */}
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                    <img
                      className="w-8 h-8 object-cover rounded-lg border border-[#D1D5DB] flex-shrink-0"
                      src={flight.airlineLogo || DefaultAirLogo}
                      alt={flightDataMap[flight.id]?.airlineName || "Airline"}
                      onError={(e) => (e.currentTarget.src = DefaultAirLogo)}
                    />
                    <div className="min-w-0">
                      <p className="text-[#262626] text-base font-semibold leading-tight truncate">{flight.airlineName || "Unknown"}</p>
                      <div className="flex items-center gap-1.5 min-w-0 mt-0.5">
                        <p className="text-[#737C8C] text-sm">{flight.flightNo}</p>
                      </div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="min-w-0">
                    <p className="text-[#5E5E5E] text-[11px] font-medium font-inter">Date</p>
                    <p className="text-[#262626] text-[13px] font-medium font-inter mt-0.5 whitespace-nowrap">
                      {formatCreatedDate(flight.departure)}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="min-w-0">
                    <p className="text-[#5E5E5E] text-[11px] font-medium font-inter">Status</p>
                    <p
                      className={`text-[13px] font-medium font-inter py-1 px-3 mt-0 -ml-3 rounded-full w-fit capitalize ${
                        statusPillClasses[flight.status] || statusPillClasses.default
                      }`}
                    >
                      {flight.status}
                    </p>
                  </div>

                  {/* Departure */}
                  <div className="flex flex-col items-center min-w-0">
                    <p className="text-[#262626] text-lg font-medium">{flight.sourceCode}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className="st-top-right text-[#737C8C] text-[8px]" />
                      <p className="text-[#737C8C] text-xs">
                        {flight.departure
                          ? new Date(flight.departure).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>

                  {/* Center Path */}
                  <div className="flex flex-col items-center min-w-0">
                    <p className="text-[#737C8C] text-xs text-center mb-1">
                      {flight?.elptim && (
                        <span>
                          {flight.elptim.includes(":")
                            ? `${flight.elptim.split(":")[0]}h ${flight.elptim.split(":")[1]}m`
                            : `${parseInt(flight.elptim.slice(0, -2))}h ${parseInt(
                                flight.elptim.slice(-2)
                              )}m`}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center w-full">
                      <div className="w-2 h-2 rounded-full border-2 border-[#FBBF24] flex-shrink-0" />
                      <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
                      <i className="st-flight-slim text-[#262626] text-sm mx-1" />
                      <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
                      <div className="w-2 h-2 rounded-full border-2 border-[#FBBF24] flex-shrink-0" />
                    </div>
                  </div>

                  {/* Arrival */}
                  <div className="flex flex-col items-center min-w-0">
                    <p className="text-[#262626] text-lg font-medium">{flight.destinationCode}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className="st-bottom-right text-[#737C8C] text-[8px]" />
                      <p className="text-[#737C8C] text-xs">
                        {flight.arrival
                          ? new Date(flight.arrival).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>

                  </div>
                </div>

                {isLoading[flight.id] ? (
                  <AnsweredFlightSkeleton />
                ) : (
                  (flight.status === "Answered" || flight.status === "Updated Answer") && (
                    <div className="hidden md:block px-2 pb-2">
                      <div className="border-t border-gray-300 px-4 py-2.5 flex items-end justify-end gap-2">
                        <div className="flex flex-col gap-1 w-[150px] flex-shrink-0 mr-3 lg:mr-4 xl:mr-6">
                          <p className="text-lightgray-200 text-xs">Answered By</p>
                          <p className="text-grease-100 text-sm font-medium">
                            {flightDataMap[flight.id]?.responderName || "Unknown"}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-lightgray-150">
                            <i className="st-time"></i>
                            <p>{getTimeAgo(flight.modifiedDate)}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0 mr-auto">
                          <p className="text-lightgray-200 text-xs">Open Seats</p>
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.firstClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">First</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.busClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">Bus</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.ecoPlus || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco+</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.eco || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco</p>
                            </div>
                            <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.total || 0}</p>
                              <p className="text-lightgray-150 text-xs">Total</p>
                            </div>
                          </div>
                        </div>
                        <div className="w-px h-10 bg-lightgray-100 flex-shrink-0"></div>
                        <div className="flex flex-col gap-2 flex-shrink-0 mr-4 lg:mr-8 xl:mr-8">
                          <p className="text-lightgray-200 text-xs">Listed Non-Rev Passengers</p>
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.nonFirstClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">First</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.nonBusClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">Bus</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.nonEcoPlus || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco+</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.nonEco || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco</p>
                            </div>
                            <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight.nonRevListed || 0}</p>
                              <p className="text-lightgray-150 text-xs">Total</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-4 border border-gray-200 bg-white h-[41px] pr-1 pl-4 rounded-lg w-[234px] flex-shrink-0 ml-auto">
                          <p className="text-[#262626] text-lg font-semibold">{flight.flightNo}</p>
                          <p className="text-white bg-[#009C6D] rounded px-3 py-1.5 text-sm font-medium capitalize">Scheduled</p>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ))
          )}
        </div>
      </details>
    </section>
  );
};

export default FlightListing;
